import get from "lodash.get";
import curry from "lodash.curry";
import {
  conditionalOperator,
  logicalAndOperator,
  logicalOrOperator,
  logicalNotOperator,
  plusOperator,
  equalsOperator
} from "./operators";

//applicative
export function pure(x) {
  return () => x;
}

export function apply(fnFunctor, valueFunctor) {
  return (...rest) => {
    const fn = fnFunctor(...rest);
    const value = typeof valueFunctor === "function" ? valueFunctor(...rest) : valueFunctor;
    const result = fn(value);
    return result;
  };
}

//functor
export const fmap = curry(function fmap(fn, valueFunctor) {
  return apply(pure(fn), valueFunctor);
});

//monoid
export function identity(...rest) {
  return rest[rest.length - 1];
}

export const mappend = curry(function mappend(f, g) {
  return (...args) => g(...args.slice(0, args.length - 1), f(...args));
});

export function concat(...fns) {
  return fns.reduce(mappend);
}

//monad
export function flatten(monad) {
  return (...rest) => monad(...rest)(...rest);
}

export const bind = curry(function bind(monad, f) {
  return flatten((...rest) => f(monad(...rest)));
});
// Version of bind without flatten
//export const bind = curry((monad, f) => (...rest) => f(monad(...rest))(...rest));

export const composeK = curry(function composeK(f, g) {
  return x => bind(f(x), g);
});

export const liftM = curry(function liftM(f, monad) {
  return bind(monad, x => pure(f(x)));
});

export const liftM2 = curry(function liftM2(f, monad1, monad2) {
  return bind(monad1, x1 => bind(monad2, x2 => pure(f(x1)(x2))));
});

export const liftM3 = curry(function liftM3(f, monad1, monad2, monad3) {
  return bind(monad1, x1 => bind(monad2, x2 => bind(monad3, x3 => pure(f(x1)(x2)(x3)))));
});

const lift2 = curry(function lift2(op, applicative1, applicative2) {
  return apply(fmap(op, applicative1), applicative2);
});

const lift3 = curry(function lift3(op, applicative1, applicative2, applicative3) {
  return apply(apply(fmap(op, applicative1), applicative2), applicative3);
});

export const not = fmap(logicalNotOperator);
export const isNotANumber = fmap(isNaN);
export const min = lift2(curry(Math.min));
export const max = lift2(curry(Math.max));
export const both = lift2(logicalAndOperator);
export const either = lift2(logicalOrOperator);
export const sum = lift2(plusOperator);
export const equals = lift2(equalsOperator);
export const ifThenElse = lift3(conditionalOperator);

export function isNumber(f) {
  return not(isNotANumber(f));
}

export const notEquals = curry(function notEquals(f, g) {
  return not(equals(f, g));
});

export const neither = curry(function neither(f, g) {
  return not(either(f, g));
});

export const when = curry(function when(f, g) {
  return ifThenElse(f, g, identity);
});

export function nullWhen(conditionExpression) {
  return when(conditionExpression, null);
}

export function falseWhen(conditionExpression) {
  return when(conditionExpression, false);
}

export function minimumValue(minimumExpr) {
  return max(minimumExpr, identity);
}

export function maximumValue(maximumExpr) {
  return min(maximumExpr, identity);
}

export const valueChanged = curry(function valueChanged(fn, document, prevDocument) {
  return fn(document) !== fn(prevDocument);
});

export const prop = curry(function prop(path, doc) {
  return get(doc, path);
});

export function propChanged(path) {
  return valueChanged(prop(path));
}
